import React, { forwardRef, useCallback, useMemo, useState } from "react";

//deps
import { CouponInfo, ICouponCard } from "@core-ui/interfaces/Common";

//styles
import { TitleContainer, CouponCardBanner, GiftingCouponSectionFooterImage } from "./styles";

//components
import { CouponCard } from "@core-ui/components/common/coupon-card";
import { CartGroupTitle } from "@core-ui/components/common/cart-group";
import { Image } from "@swiggy-private/dls-web-core/dist/components/Image";

//constants
import { LOCAL_STORAGE_KEYS } from "@core-ui/constants/StorageKeys";
import { COUPON_CARD_GROUP_NEW_LOTTIE_MAXLIMIT } from "@core-ui/constants/Coupon";
import { COUPON_CARD_TESTS_IDS } from "@swiggy-private/cart-test-ids";
import { COUPON_GROUP_TYPES, CouponCardImpressionPayload } from "@swiggy-private/core-types";

//helpers
import { LocalStorage } from "@core-ui/helpers/LocalStorage";
import { isEmpty } from "@core-ui/helpers/Utils";
import { getTransformedHtmlString, isRecommendedCouponGroup } from "@core-ui/helpers/Coupon";

//hooks
import { useEffectOnce } from "@core-ui/hooks/UseEffectOnce";
import { TitleTag } from "./components";

export interface CouponCardGroupProps {
    couponGroup: ICouponCard;
    onApplyCouponClick: (payload: CouponCardImpressionPayload) => void;
    clickMoreDetailsEvent?: (couponCode: string, couponType: string) => void;
    couponOffersImpressionEvent: (payload: CouponCardImpressionPayload) => void;
    rewardOfferImpressionEvent: (payload: CouponCardImpressionPayload) => void;
    paymentBannerImpressionEvent: (bannerText: string) => void;
    onImpressionRemoveCoupon?: VoidFunction;
    isPaymentCouponListingPage?: boolean;
    showTncCouponHalfcard?: (coupon: CouponInfo) => void;
    isGiftingCouponListing?: boolean;
    couponSelections?: Map<string, boolean>;
    onCouponCheckboxClick?: (couponId: string, businessLine?: string) => void;
}

const CouponCardGroup = forwardRef<HTMLDivElement, CouponCardGroupProps>(
    (
        {
            couponGroup,
            onApplyCouponClick,
            clickMoreDetailsEvent,
            couponOffersImpressionEvent,
            rewardOfferImpressionEvent,
            paymentBannerImpressionEvent,
            onImpressionRemoveCoupon,
            isPaymentCouponListingPage,
            showTncCouponHalfcard,
            isGiftingCouponListing,
            couponSelections,
            onCouponCheckboxClick,
        }: CouponCardGroupProps,
        ref,
    ) => {
        const { type, newSectionIndicator, message } = couponGroup;
        const isRecommendedCouponSection = isRecommendedCouponGroup(type);
        const couponBannerHtmlString = getTransformedHtmlString(message?.text);
        const newBadgeLottieImpressionCount =
            LocalStorage.getItem(LOCAL_STORAGE_KEYS.COUPON_CARD_NEW_BADGE_IMPRESSION_COUNT, 0) || 0;
        const showNewBadgeLottieDefaultValue =
            newBadgeLottieImpressionCount >= 0 &&
            newBadgeLottieImpressionCount <= COUPON_CARD_GROUP_NEW_LOTTIE_MAXLIMIT;
        const [showNewBadgeLottie, setShowNewBadgeLottie] = useState(showNewBadgeLottieDefaultValue);

        const showNewTagLottie = useMemo(
            () => showNewBadgeLottie && newSectionIndicator,
            [showNewBadgeLottie, newSectionIndicator],
        );

        const applyCouponHandler = useCallback(
            (payload: CouponCardImpressionPayload) => {
                const { couponCode, offerId, isApplicable, couponType, type } = payload;
                onApplyCouponClick({ couponCode, offerId, isApplicable, couponType, type });
            },
            [onApplyCouponClick],
        );

        /**
         * For gifting we needed to render (+) icons as footer image between different coupon sections
         * Due to constraints on PL end they are unable to couple the footer image with the coupon section and hide/show asset based on next coupon section availability
         * As within portal we track isLastCard flag for each coupon section, we are using it to render footer image only for the non - last coupon section
         * This is a temporary solution and will be removed once PL is able to couple the footer image with the coupon section and hide/show asset based on next coupon section availability
         */
        const renderFooterImage = useMemo(() => {
            return (
                !isEmpty(couponGroup.metaInfo?.footerImage?.assetId) &&
                !couponGroup?.isLastCard &&
                isGiftingCouponListing
            );
        }, [couponGroup?.isLastCard, couponGroup?.metaInfo?.footerImage?.assetId, isGiftingCouponListing]);

        useEffectOnce(() => {
            if (!isEmpty(message) && couponBannerHtmlString) {
                paymentBannerImpressionEvent(message.text);
            }
            if (type === COUPON_GROUP_TYPES.APPLIED_OFFERS) {
                onImpressionRemoveCoupon?.();
            }
        });

        return (
            <>
                <TitleContainer ref={ref}>
                    <CartGroupTitle data-testid={COUPON_CARD_TESTS_IDS.GROUP_TITLE} title={couponGroup.title} />
                    <TitleTag
                        showNewTagLottie={showNewTagLottie}
                        isRecommendedCouponSection={isRecommendedCouponSection}
                        setShowNewBadgeLottie={setShowNewBadgeLottie}
                    />
                </TitleContainer>
                {!isEmpty(message) && couponBannerHtmlString && (
                    <CouponCardBanner
                        variant="V2.body2.neutral.medium"
                        dangerouslySetInnerHTML={{ __html: couponBannerHtmlString }}
                        style={{
                            color: message?.textColor,
                            border: message?.borderColor ? `1px solid ${message.borderColor}` : undefined,
                            background: `linear-gradient(90deg, ${message.bgColor[0]} 0%, ${message.bgColor[1]} 100%)`,
                        }}
                    />
                )}

                {couponGroup.coupons?.map((coupon: CouponInfo, index: number) => {
                    const couponId = coupon.id || "";
                    const isApplied = (couponSelections?.get(couponId) ?? coupon.isSelected) || false;

                    // Create a wrapper function that includes the business line
                    const handleCheckboxClick = (id: string) => {
                        onCouponCheckboxClick?.(id, coupon.businessLine);
                    };

                    return (
                        <CouponCard
                            key={coupon?.id || index}
                            isLastCouponInGroup={index === (couponGroup.coupons?.length || 0) - 1} // used in styles
                            type={type}
                            data={coupon}
                            onApplyCouponClick={applyCouponHandler}
                            clickMoreDetailsEvent={clickMoreDetailsEvent}
                            couponOffersImpressionEvent={couponOffersImpressionEvent}
                            rewardOfferImpressionEvent={rewardOfferImpressionEvent}
                            isPaymentListingCouponCard={isPaymentCouponListingPage}
                            showTncCouponHalfcard={showTncCouponHalfcard}
                            isGiftingCouponListing={isGiftingCouponListing}
                            actionType={couponGroup.metaInfo?.couponSelectionStyle}
                            isApplied={isApplied}
                            onCheckboxClick={handleCheckboxClick}
                        />
                    );
                })}
                {renderFooterImage && (
                    <GiftingCouponSectionFooterImage
                        data-testid={COUPON_CARD_TESTS_IDS.FOOTER_IMAGE}
                        aspectRatio={couponGroup.metaInfo?.footerImage?.aspectRatio}
                    >
                        <Image
                            cloudinaryId={couponGroup.metaInfo?.footerImage?.assetId}
                            width="100%"
                            height="100%"
                            fit="contain"
                            cloudinaryHeight={100}
                        />
                    </GiftingCouponSectionFooterImage>
                )}
            </>
        );
    },
);

if (__DEV__) CouponCardGroup.displayName = "CouponCardGroup";

export default CouponCardGroup;
